import { useCurrentEditor } from '@tiptap/react';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';
import { useBaseWorkerAttributes } from '@/hooks/use-base-worker-attributes';
import { WorkerAttr } from '@/types/worker-attr';
import { cn } from '@/utils/classnames';

import { getTagPath } from '../../utils/get-tag-path';

type EditWorkerAttributeProps = {
  onChange: (newAttr: Partial<WorkerAttr>) => void;
  path: string;
} & WorkerAttr;

export default function EditWorkerAttribute({
  fallback,
  path,
  onChange,
}: EditWorkerAttributeProps) {
  const intl = useIntl();
  const listboxId = useId();
  const attributes = useBaseWorkerAttributes();
  const getAttrFromId = (path: string) =>
    attributes.find(a => getTagPath(a) === path);

  const [fallbackValue, setFallbackValue] = useState(fallback ?? '');
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const actualInputRef = useRef<HTMLInputElement | null>(null);
  const { editor } = useCurrentEditor();
  const wasEditableRef = useRef<boolean>(true);

  const hasFallback = !!fallbackValue;
  const selectedAttr = getAttrFromId(path);
  const selectedLabel = selectedAttr?.label ?? '';

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!selectOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the select dropdown
      if (selectRef.current && selectRef.current.contains(target)) {
        return;
      }

      // Close the dropdown
      setSelectOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectOpen]);

  // Find and store reference to actual input element
  useEffect(() => {
    const inputWrapper = inputRef.current;
    if (!inputWrapper) return;

    const findActualInput = () => {
      return (
        (inputWrapper.querySelector(
          'input[data-slot="input"]',
        ) as HTMLInputElement) ||
        (inputWrapper.querySelector('input') as HTMLInputElement)
      );
    };

    // Try to find input immediately
    const actualInput = findActualInput();
    if (!actualInput) {
      // If not found, try again after a short delay
      const timer = setTimeout(() => {
        const resolvedActualInput = findActualInput();
        if (resolvedActualInput) {
          actualInputRef.current = resolvedActualInput;
        }
      }, 100);
      // eslint-disable-next-line consistent-return
      return () => clearTimeout(timer);
    }

    actualInputRef.current = actualInput;
  }, []);

  // Disable editor when input is focused to prevent TipTap from processing keyboard events
  useEffect(() => {
    const actualInput = actualInputRef.current;
    if (!actualInput || !editor) return;

    const handleFocus = () => {
      // Save current editable state
      wasEditableRef.current = editor.isEditable;
      // Blur the editor to remove focus from it
      editor.view.dom.blur();
      // Disable editor to prevent it from processing keyboard events
      editor.setEditable(false);
    };

    const handleBlur = () => {
      // Re-enable editor when input loses focus
      if (wasEditableRef.current) {
        editor.setEditable(true);
      }
    };

    // Add capture phase listener to prevent TipTap from intercepting input events
    const handleMouseDownCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target === actualInput ||
        actualInput.contains(target) ||
        target.closest('[data-worker-attr-edit="true"]') ||
        target.closest('[data-slot="input"]')
      ) {
        // Prevent Tippy/editor from handling clicks on input
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Blur editor immediately
        if (editor) {
          editor.view.dom.blur();
        }
        // Focus the input
        requestAnimationFrame(() => {
          actualInput.focus();
        });
      }
    };

    actualInput.addEventListener('focus', handleFocus);
    actualInput.addEventListener('blur', handleBlur);
    // Add capture phase listener to document to intercept before TipTap
    document.addEventListener('mousedown', handleMouseDownCapture, true);

    // eslint-disable-next-line consistent-return
    return () => {
      actualInput.removeEventListener('focus', handleFocus);
      actualInput.removeEventListener('blur', handleBlur);
      document.removeEventListener('mousedown', handleMouseDownCapture, true);
      // Make sure to re-enable editor on cleanup
      if (wasEditableRef.current && editor) {
        editor.setEditable(true);
      }
    };
  }, [editor]);

  return (
    <div
      className="flex w-[360px] flex-col rounded-md border border-gray-300 bg-gray-100 p-2 shadow-lg"
      data-worker-attr-edit="true"
    >
      {!hasFallback && (
        <div className="mb-4 flex gap-3 rounded-sm bg-amber-50 px-3 py-2 text-amber-600">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <Typography variant="body1" className="text-sm">
            <FormattedMessage
              defaultMessage="Setting a fallback value helps prevent a message from being sent with a blank space."
              description="Warning banner when a merge field has no default if data is missing"
            />
          </Typography>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="relative" ref={selectRef}>
          <Typography variant="subtitle2" className="mb-2">
            <FormattedMessage
              defaultMessage="Attribute"
              description="Section title: which applicant profile field to merge into the message"
            />
          </Typography>

          <Button
            variant="secondary"
            role="combobox"
            aria-expanded={selectOpen}
            aria-controls={listboxId}
            tabIndex={0}
            className="w-full justify-between"
            onClick={() => setSelectOpen(!selectOpen)}
          >
            {selectedLabel ||
              intl.formatMessage({
                defaultMessage: 'Select an attribute',
                description:
                  'Placeholder on worker merge-field picker before user chooses a field',
              })}
            <ChevronDown
              className={cn(
                'ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform',
                selectOpen && 'rotate-180',
              )}
            />
          </Button>

          {selectOpen && (
            <div
              id={listboxId}
              className="absolute z-[10002] mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg"
            >
              <div
                className="max-h-60 overflow-auto overscroll-contain p-1"
                style={{
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                }}
                onWheel={e => {
                  // Stop propagation to prevent Tippy from interfering with scroll
                  e.stopPropagation();
                }}
              >
                {attributes.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    <FormattedMessage
                      defaultMessage="No attribute found."
                      description="Empty state in merge-field list when search has no match"
                    />
                  </div>
                ) : (
                  attributes.map(attr => {
                    const attrPath = getTagPath(attr);
                    const isSelected = path === attrPath;
                    return (
                      <button
                        key={attrPath}
                        type="button"
                        className={cn(
                          'w-full rounded-sm px-3 py-2 text-left text-sm transition-colors',
                          isSelected
                            ? 'bg-blue-50 font-medium text-blue-700'
                            : 'text-gray-900 hover:bg-gray-100',
                        )}
                        onMouseDown={e => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={() => {
                          const newAttr = getAttrFromId(attrPath);
                          if (!newAttr) return;

                          onChange(newAttr);
                          setFallbackValue(newAttr.fallback ?? '');
                          setSelectOpen(false);
                        }}
                      >
                        {attr.label}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <Typography variant="subtitle2" className="mb-1">
            <FormattedMessage
              defaultMessage="Fallback"
              description="Subsection: string to show if the selected attribute is empty"
            />
          </Typography>

          <Typography variant="body1" className="mb-1 text-sm text-gray-600">
            <FormattedMessage
              defaultMessage="Text displayed if no value exists"
              description="Helper text explaining the fallback string below"
            />
          </Typography>

          <div className="relative" style={{ pointerEvents: 'auto' }}>
            <Input
              ref={inputRef}
              onChange={event => {
                event.stopPropagation();
                onChange({ fallback: event.target.value });
                setFallbackValue(event.target.value);
              }}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter fallback text',
                description:
                  'Input placeholder: optional default if merge data is missing',
              })}
              value={fallbackValue}
              className="text-gray-900"
              style={{
                color: '#111827',
                pointerEvents: 'auto',
                cursor: 'text',
              }}
              onFocus={e => {
                e.stopPropagation();
                // Ensure editor is blurred when input gets focus
                if (editor) {
                  editor.view.dom.blur();
                }
              }}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                // Use native event for stopImmediatePropagation
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                // Blur editor immediately
                if (editor) {
                  editor.view.dom.blur();
                }
                // Find actual input and focus it
                const actualInput =
                  (inputRef.current?.querySelector(
                    'input[data-slot="input"]',
                  ) as HTMLInputElement) ||
                  (inputRef.current?.querySelector(
                    'input',
                  ) as HTMLInputElement);
                if (actualInput) {
                  requestAnimationFrame(() => {
                    actualInput.focus();
                    // Try again in case first attempt failed
                    setTimeout(() => {
                      if (document.activeElement !== actualInput) {
                        actualInput.focus();
                      }
                    }, 10);
                  });
                }
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                // Use native event for stopImmediatePropagation
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                // Blur editor immediately
                if (editor) {
                  editor.view.dom.blur();
                }
                // Find actual input and focus it
                const actualInput =
                  (inputRef.current?.querySelector(
                    'input[data-slot="input"]',
                  ) as HTMLInputElement) ||
                  (inputRef.current?.querySelector(
                    'input',
                  ) as HTMLInputElement);
                if (actualInput) {
                  requestAnimationFrame(() => {
                    actualInput.focus();
                    actualInput.select();
                    // Try again in case first attempt failed
                    setTimeout(() => {
                      if (document.activeElement !== actualInput) {
                        actualInput.focus();
                      }
                    }, 10);
                  });
                }
              }}
              onPointerDown={e => {
                e.preventDefault();
                e.stopPropagation();
                // Use native event for stopImmediatePropagation
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                if (editor) {
                  editor.view.dom.blur();
                }
                // Find actual input and focus it
                const actualInput =
                  (inputRef.current?.querySelector(
                    'input[data-slot="input"]',
                  ) as HTMLInputElement) ||
                  (inputRef.current?.querySelector(
                    'input',
                  ) as HTMLInputElement);
                if (actualInput) {
                  requestAnimationFrame(() => {
                    actualInput.focus();
                    // Try again in case first attempt failed
                    setTimeout(() => {
                      if (document.activeElement !== actualInput) {
                        actualInput.focus();
                      }
                    }, 10);
                  });
                }
              }}
              onKeyDown={e => {
                // Stop propagation to prevent TipTap from intercepting
                e.stopPropagation();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
